import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';

/**
 * SHAR-917 — a numeric field that REFUSES letters and symbols as they are typed.
 *
 * antd's InputNumber accepts any character into the text box and strips what it
 * cannot parse when the field loses focus. QA saw exactly that on the ShareJet
 * margin: "abc" could be typed, sat there while the form was being filled in,
 * and vanished afterwards. The value that reached the API was always a number,
 * so this was never a data defect — it is what the admin sees while typing, and
 * the ticket asks for the input to be blocked outright.
 *
 * What is allowed: digits, one decimal separator (a dot or a comma, because a UK
 * keyboard offers both and antd reads both), and a leading minus when the field
 * itself admits negatives. Everything else is refused at the keystroke.
 *
 * What is NOT touched: editing keys, selection and the clipboard shortcuts, so
 * Backspace, the arrows, Tab, Home/End, Ctrl/Cmd+A/C/V/X/Z still work. A PASTE is
 * checked as a whole rather than per character: pasting "12.5" is fine, pasting
 * "£12.5" or "twelve" does nothing at all, which is the same rule the keyboard
 * gets and avoids the half-pasted value a per-character filter would leave.
 *
 * Deliberately a drop-in for InputNumber: same props, same look, so the numeric
 * fields of the panel keep behaving identically in every other respect.
 */
const EDIT_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End', 'PageUp', 'PageDown',
]);

function allowsNegative(min: InputNumberProps['min']): boolean {
  return min != null && Number(min) < 0;
}

/** Digits, at most one separator, and a leading minus only where one is allowed. */
function pattern(min: InputNumberProps['min']): RegExp {
  return allowsNegative(min) ? /^-?\d*[.,]?\d*$/ : /^\d*[.,]?\d*$/;
}

export default function NumberInput(props: InputNumberProps) {
  const { onKeyDown, onPaste, min, ...rest } = props;

  return (
    <InputNumber
      {...rest}
      min={min}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        // A shortcut (Ctrl/Cmd + something) is not text: leave it alone.
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (EDIT_KEYS.has(e.key)) return;
        // A key that types more than one character is a named key we have not
        // listed (F5, Shift, CapsLock...). Those type nothing, so let them be.
        if (e.key.length !== 1) return;
        // Copilot review — JUDGE THE RESULT, NOT THE KEY. Accepting every '.' or
        // '-' on its own let "12.3.4" and "12-3" be typed, which is the same
        // invalid text this control exists to keep out of the box. Building the
        // value the keystroke would produce — at the caret, over any selection —
        // and testing that is one rule for the keyboard and the clipboard both.
        const el = e.currentTarget as unknown as HTMLInputElement;
        const value = el?.value ?? '';
        const from = el?.selectionStart ?? value.length;
        const to = el?.selectionEnd ?? from;
        const next = value.slice(0, from) + e.key + value.slice(to);
        if (!pattern(min).test(next)) e.preventDefault();
      }}
      onPaste={(e) => {
        onPaste?.(e);
        if (e.defaultPrevented) return;
        // Copilot review — JUDGE THE RESULT, as the keystroke path does.
        //
        // Testing the pasted fragment alone let a paste that is valid on its own
        // make the field invalid: with "12.3" in the box and the caret at the
        // end, pasting "." passed — a second separator, which is exactly what
        // this control exists to keep out. The clipboard is now applied at the
        // caret, over any selection, and the value it would produce is tested.
        const text = e.clipboardData.getData('text').trim();
        const el = e.currentTarget as unknown as HTMLInputElement;
        const value = el?.value ?? '';
        const from = el?.selectionStart ?? value.length;
        const to = el?.selectionEnd ?? from;
        const next = value.slice(0, from) + text + value.slice(to);
        if (!pattern(min).test(next)) e.preventDefault();
      }}
    />
  );
}
